/**
 * Step 3: Summary & Preview
 * 
 * Final review of campaign details:
 * - Basic Information summary
 * - Linked Promos summary
 * - Funding information
 * - Edit links to previous steps
 * - Save as Draft or Activate Now actions
 */

import React from 'react';
import { CampaignFormData, CampaignValidationErrors, CampaignStatus } from '../../../../../api/promos/types';
import './StepperButtons.css';
import './SummaryCardStyles.css';

interface StepSummaryProps {
  formData: CampaignFormData;
  validationErrors: CampaignValidationErrors;
  isSubmitting: boolean;
  onPrevious: () => void;
  onSaveDraft: () => void;
  onActivate: () => void;
  onPause?: () => void;
  onArchive?: () => void;
  onEditStep: (stepIndex: number) => void;
  isEditMode?: boolean;
  hasChanges?: boolean;
  originalStatus?: CampaignStatus | null;
}

const StepSummary: React.FC<StepSummaryProps> = ({
  formData,
  validationErrors,
  isSubmitting,
  onPrevious,
  onSaveDraft,
  onActivate,
  onPause,
  onArchive,
  onEditStep,
  isEditMode = false,
  hasChanges = false,
  originalStatus = null,
}) => {
  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div>
      {/* Step Header */}
      <div className="mb-4">
        <h5 className="mb-2">Summary & Preview</h5>
        <p className="text-muted mb-0">
          Review your campaign details before saving or activating
        </p>
      </div>

      {/* Basic Information Summary */}
      <div className="summary-card basic-information">
        <div className="summary-card-header">
          <h6 className="summary-card-title">Basic Information</h6>
          <button
            type="button"
            className="summary-card-edit-button"
            onClick={() => onEditStep(0)}
          >
            <i className="ph-pencil"></i>
            Edit
          </button>
        </div>
        <div className="summary-card-body three-column">
          <div className="summary-field">
            <label className="summary-field-label">Campaign Name</label>
            <div className="summary-field-content">{formData.name}</div>
          </div>
          <div className="summary-field">
            <label className="summary-field-label">Campaign Type</label>
            <div className="summary-field-content">
              {formData.campaignType.charAt(0) + formData.campaignType.slice(1).toLowerCase()}
            </div>
          </div>
          <div className="summary-field">
            <label className="summary-field-label">Valid From Date</label>
            <div className="summary-field-content">{formatDate(formData.startAt)}</div>
          </div>
          <div className="summary-field">
            <label className="summary-field-label">Expiry Date</label>
            <div className="summary-field-content">{formatDate(formData.endAt)}</div>
          </div>
          {formData.description && (
            <div className="summary-field full-width">
              <label className="summary-field-label">Description</label>
              <div className="summary-field-content" style={{ fontWeight: 400, color: '#6B7280' }}>
                {formData.description}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Linked Promos Summary - Only show if promos are linked */}
      {formData.linkedPromos && formData.linkedPromos.length > 0 && (
        <div className="summary-card">
          <div className="summary-card-header">
            <h6 className="summary-card-title">Linked Promos</h6>
            <button
              type="button"
              className="summary-card-edit-button"
              onClick={() => onEditStep(1)}
            >
              <i className="ph-pencil"></i>
              Edit
            </button>
          </div>
          <div className="summary-card-body">
            {formData.linkedPromos.map((promoId, index) => (
              <div key={index} className="d-flex align-items-center" style={{ gap: '8px' }}>
                <i className="ph-check-circle text-success" style={{ fontSize: '18px', color: '#16A34A' }}></i>
                <span className="summary-field-content">{promoId}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons - Conditional rendering based on edit mode and changes */}
      <div className="stepper-buttons-container">
        {/* Save as Draft - Show if creating new, has changes, or is draft status */}
        <div className="stepper-buttons-left">
          {(!isEditMode || hasChanges || originalStatus === CampaignStatus.DRAFT) && (
            <button
              type="button"
              className="btn btn-link text-primary p-0 text-decoration-none"
              onClick={onSaveDraft}
              disabled={isSubmitting}
              style={{ 
                fontFamily: 'Helvetica Neue, Arial, sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                color: '#006EFF'
              }}
            >
              {isSubmitting ? 'Saving...' : 'Save as Draft'}
            </button>
          )}
        </div>
        
        <div className="stepper-buttons-right" style={{ gap: '12px', marginLeft: 'auto' }}>
          {/* Schedule button - Always disabled as functionality is not yet implemented */}
          <button
            type="button"
            className="stepper-schedule-button"
            onClick={() => {}}
            disabled={true}
          >
            Schedule
          </button>
          
          {/* Conditional button rendering based on edit mode, changes, and status */}
          {isEditMode && !hasChanges ? (
            // Edit mode + No changes: Check status to determine button
            originalStatus === CampaignStatus.DRAFT ? (
              // DRAFT status + No changes: Show Activate Now button
              <button
                type="button"
                className="stepper-activate-button"
                onClick={onActivate}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Activating...
                  </>
                ) : (
                  'Activate Now'
                )}
              </button>
            ) : originalStatus === CampaignStatus.ACTIVE && onPause ? (
              // ACTIVE status + No changes: Show Pause button
              <button
                type="button"
                className="stepper-activate-button"
                onClick={onPause}
                disabled={isSubmitting}
                style={{ backgroundColor: '#DC2626', borderColor: '#DC2626' }}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Pausing...
                  </>
                ) : (
                  'Pause'
                )}
              </button>
            ) : originalStatus === CampaignStatus.PAUSED ? (
              // PAUSED status + No changes: Show both Activate and Archive buttons
              <>
                <button
                  type="button"
                  className="stepper-activate-button"
                  onClick={onActivate}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Activating...
                    </>
                  ) : (
                    'Activate'
                  )}
                </button>
                {onArchive && (
                  <button
                    type="button"
                    className="stepper-activate-button"
                    onClick={onArchive}
                    disabled={isSubmitting}
                    style={{ backgroundColor: '#DC2626', borderColor: '#DC2626' }}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Archiving...
                      </>
                    ) : (
                      'Archive'
                    )}
                  </button>
                )}
              </>
            ) : onArchive ? (
              // INACTIVE/ARCHIVED status + No changes: Show Archive button only
              <button
                type="button"
                className="stepper-activate-button"
                onClick={onArchive}
                disabled={isSubmitting}
                style={{ backgroundColor: '#DC2626', borderColor: '#DC2626' }}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Archiving...
                  </>
                ) : (
                  'Archive'
                )}
              </button>
            ) : null
          ) : (
            // Edit mode + Has changes OR Create mode: Show Update/Activate button
            <button
              type="button"
              className="stepper-activate-button"
              onClick={onActivate}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {isEditMode && hasChanges 
                    ? (originalStatus === CampaignStatus.DRAFT ? 'Activating...' : 'Updating...')
                    : 'Activating...'}
                </>
              ) : (
                isEditMode && hasChanges
                  ? (originalStatus === CampaignStatus.DRAFT ? 'Activate' : 'Update')
                  : 'Activate Now'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepSummary;